import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Users, X, Copy, Mail, Check, Search, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDraftIntroduction } from '@/hooks/use-sharing'
import { useContacts } from '@/hooks/use-contacts'
import type { Contact, IntroductionDraft } from '@/lib/types'
import { toast } from '@/lib/toast'

interface IntroductionDialogProps {
  contact: Contact
  onClose: () => void
}

export function IntroductionDialog({ contact, onClose }: IntroductionDialogProps) {
  const { t } = useTranslation()
  const draftIntro = useDraftIntroduction()
  const [search, setSearch] = useState('')
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [context, setContext] = useState('')
  const [draft, setDraft] = useState<IntroductionDraft | null>(null)
  const [copied, setCopied] = useState(false)

  const { data: searchResults } = useContacts(search.length >= 2 ? { q: search, limit: 5 } : {})

  const filteredResults = searchResults?.items?.filter((c) => c.id !== contact.id) ?? []

  const contact1Name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')

  const handleGenerate = async () => {
    if (!selectedContact) return
    const result = await draftIntro.mutateAsync({
      contactId1: contact.id,
      contactId2: selectedContact.id,
      context: context || undefined,
    })
    setDraft(result)
  }

  const handleCopy = async () => {
    if (!draft) return
    const text = `Subject: ${draft.subject}\n\n${draft.body}`
    await navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success(t('sharing.introCopied', 'Introduction copied'))
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSendEmail = () => {
    if (!draft) return
    const subject = encodeURIComponent(draft.subject)
    const body = encodeURIComponent(draft.body)
    // Pre-fill to addresses if both contacts have email
    const to = [contact.email, selectedContact?.email].filter(Boolean).join(',')
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_self')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <div className="bg-surface-container-low max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl p-5 shadow-lg sm:rounded-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="text-primary size-5" />
            <h2 className="text-lg font-semibold">
              {t('sharing.introTitle', 'Draft Introduction')}
            </h2>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        {!draft ? (
          <>
            {/* Contact 1 (fixed) */}
            <div className="mb-3">
              <label className="text-on-surface-variant text-xs font-medium">
                {t('sharing.person1', 'Person 1')}
              </label>
              <div className="bg-surface-container-high mt-1 rounded-lg px-3 py-2 text-sm font-medium">
                {contact1Name}
                {contact.company && (
                  <span className="text-on-surface-variant ml-1">at {contact.company}</span>
                )}
              </div>
            </div>

            {/* Contact 2 (search) */}
            <div className="mb-3">
              <label className="text-on-surface-variant text-xs font-medium">
                {t('sharing.person2', 'Person 2')}
              </label>
              {selectedContact ? (
                <div className="bg-surface-container-high mt-1 flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium">
                  <span>
                    {[selectedContact.firstName, selectedContact.lastName]
                      .filter(Boolean)
                      .join(' ')}
                    {selectedContact.company && (
                      <span className="text-on-surface-variant ml-1">
                        at {selectedContact.company}
                      </span>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => {
                      setSelectedContact(null)
                      setSearch('')
                    }}
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ) : (
                <div className="mt-1">
                  <div className="relative">
                    <Search className="text-on-surface-variant absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t('sharing.searchContact', 'Search for a contact...')}
                      className="bg-surface-container-high text-on-surface placeholder:text-on-surface-variant/50 focus:ring-primary/30 w-full rounded-lg py-2 pr-3 pl-9 text-sm outline-none focus:ring-2"
                    />
                  </div>
                  {search.length >= 2 && filteredResults.length > 0 && (
                    <div className="bg-surface-container-highest border-outline-variant/20 mt-1 max-h-40 overflow-y-auto rounded-lg border shadow">
                      {filteredResults.map((c) => (
                        <button
                          key={c.id}
                          className="hover:bg-surface-container-high flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                          onClick={() => {
                            setSelectedContact(c)
                            setSearch('')
                          }}
                        >
                          {c.avatarUrl ? (
                            <img
                              src={c.avatarUrl}
                              className="size-6 rounded-full object-cover"
                              alt=""
                            />
                          ) : (
                            <div className="bg-primary/10 text-primary flex size-6 items-center justify-center rounded-full text-xs font-semibold">
                              {c.firstName?.[0]}
                            </div>
                          )}
                          <span>
                            {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                            {c.company && (
                              <span className="text-on-surface-variant ml-1 text-xs">
                                at {c.company}
                              </span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Context */}
            <div className="mb-4">
              <label className="text-on-surface-variant text-xs font-medium">
                {t('sharing.introContext', 'Additional context (optional)')}
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={t(
                  'sharing.introContextPlaceholder',
                  'Why should they meet? What do they have in common?',
                )}
                className="bg-surface-container-high text-on-surface placeholder:text-on-surface-variant/50 focus:ring-primary/30 mt-1 w-full resize-none rounded-lg p-3 text-sm outline-none focus:ring-2"
                rows={3}
              />
            </div>

            <Button
              onClick={handleGenerate}
              disabled={!selectedContact || draftIntro.isPending}
              className="w-full"
            >
              <Sparkles className="mr-1.5 size-4" />
              {draftIntro.isPending
                ? t('sharing.drafting', 'Drafting...')
                : t('sharing.generateIntro', 'Generate Introduction')}
            </Button>
          </>
        ) : (
          <>
            {/* Draft result */}
            <div className="bg-surface-container-high mb-4 rounded-lg p-4">
              <p className="text-on-surface-variant mb-1 text-xs font-medium">
                {t('sharing.subject', 'Subject')}
              </p>
              <p className="mb-3 text-sm font-medium">{draft.subject}</p>
              <p className="text-on-surface-variant mb-1 text-xs font-medium">
                {t('sharing.body', 'Body')}
              </p>
              <p className="text-sm whitespace-pre-wrap">{draft.body}</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleCopy}>
                {copied ? <Check className="mr-1.5 size-4" /> : <Copy className="mr-1.5 size-4" />}
                {copied ? t('sharing.copied', 'Copied!') : t('sharing.copy', 'Copy')}
              </Button>
              <Button className="flex-1" onClick={handleSendEmail}>
                <Mail className="mr-1.5 size-4" />
                {t('sharing.sendEmail', 'Send Email')}
              </Button>
            </div>

            <Button variant="ghost" className="mt-2 w-full" onClick={() => setDraft(null)}>
              {t('sharing.editIntro', 'Edit & Regenerate')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
